import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PurchaseOrder, Invoice, LineItem, POTimeEntriesDetail, ProjectTimeDetail, WeekEmployeeEntry } from '../types';
import { purchaseOrderAPI, invoiceAPI } from '../services/api';
import { calculateDueDate } from '../constants/paymentTerms';

/** Active POs for new billing, plus the invoice's current PO when editing history. */
export function purchaseOrdersForInvoicePicker(
  listed: PurchaseOrder[],
  current?: PurchaseOrder | null,
): PurchaseOrder[] {
  const byId = new Map<number, PurchaseOrder>();
  listed.forEach((po) => {
    if (po.id != null && po.status === 'active') {
      byId.set(po.id, po);
    }
  });
  if (current?.id != null && !byId.has(current.id)) {
    byId.set(current.id, current);
  }
  return Array.from(byId.values());
}

// Helper function to get last month's date range
const getLastMonthDateRange = () => {
  const today = new Date();
  const year = today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear();
  const month = today.getMonth() === 0 ? 11 : today.getMonth() - 1;
  
  const startDate = new Date(year, month, 1);
  const endDate = new Date(year, month + 1, 0); // Last day of the month
  
  return {
    start: startDate.toISOString().split('T')[0],
    end: endDate.toISOString().split('T')[0]
  };
};

// Helper function to format week ending date as MM/DD/YYYY
const formatWeekEnding = (dateString: string) => {
  const date = new Date(dateString + 'T00:00:00');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

// Extended line item that tracks associated time entry IDs
interface ExtendedLineItem extends LineItem {
  time_entry_ids?: number[];
}

// Extended Invoice type for form state
interface InvoiceFormData extends Omit<Invoice, 'line_items'> {
  line_items: ExtendedLineItem[];
}

const InvoiceForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  
  const lastMonth = getLastMonthDateRange();

  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [timeEntriesDetail, setTimeEntriesDetail] = useState<POTimeEntriesDetail | null>(null);
  const [loadingTimeEntries, setLoadingTimeEntries] = useState(false);
  
  const [formData, setFormData] = useState<InvoiceFormData>({
    purchase_order: 0,
    issue_date: new Date().toISOString().split('T')[0],
    due_date: '',
    start_date: lastMonth.start,
    end_date: lastMonth.end,
    line_items: [],
    tax_rate: 0,
    status: 'draft',
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchPurchaseOrders();
    if (isEditMode && id) {
      fetchInvoice(parseInt(id));
    }
  }, [id, isEditMode]);
  
  // Fetch time entries when PO or date range changes
  useEffect(() => {
    if (formData.purchase_order && formData.start_date && formData.end_date) {
      fetchTimeEntries();
    }
  }, [formData.purchase_order, formData.start_date, formData.end_date]);

  const pickerPurchaseOrders = useMemo(
    () => purchaseOrdersForInvoicePicker(purchaseOrders, formData.po_details),
    [purchaseOrders, formData.po_details]
  );

  const fetchPurchaseOrders = async () => {
    try {
      const response = await purchaseOrderAPI.getAll({ status: 'active' });
      setPurchaseOrders(response.data.results.filter((po) => po.status === 'active'));
    } catch (err) {
      console.error('Failed to fetch purchase orders', err);
    }
  };

  const fetchInvoice = async (invoiceId: number) => {
    try {
      setLoading(true);
      const response = await invoiceAPI.getById(invoiceId);
      setFormData(response.data as InvoiceFormData);
      setError(null);
    } catch (err) {
      setError('Failed to fetch invoice');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchTimeEntries = async () => {
    try {
      setLoadingTimeEntries(true);
      const response = await purchaseOrderAPI.getTimeEntriesDetail(
        formData.purchase_order,
        formData.start_date,
        formData.end_date
      );
      setTimeEntriesDetail(response.data);
    } catch (err) {
      console.error('Failed to fetch time entries', err);
      setTimeEntriesDetail(null);
    } finally {
      setLoadingTimeEntries(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    if (name === 'purchase_order') {
      const poId = parseInt(value) || 0;
      const selectedPO = pickerPurchaseOrders.find(po => po.id === poId);
      
      setFormData((prev) => ({
        ...prev,
        purchase_order: poId,
        due_date: selectedPO && prev.issue_date ? calculateDueDate(
          prev.issue_date,
          selectedPO.payment_terms,
          selectedPO.custom_days
        ) : prev.due_date
      }));
    } else if (name === 'issue_date') {
      const selectedPO = pickerPurchaseOrders.find(po => po.id === formData.purchase_order);
      
      setFormData((prev) => ({
        ...prev,
        issue_date: value,
        due_date: selectedPO ? calculateDueDate(
          value,
          selectedPO.payment_terms,
          selectedPO.custom_days
        ) : prev.due_date
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: name === 'tax_rate' ? parseFloat(value) || 0 : value,
      }));
    }
  };

  const handleLineItemChange = (index: number, field: keyof LineItem, value: string | number) => {
    const newLineItems = [...formData.line_items];
    newLineItems[index] = {
      ...newLineItems[index],
      [field]: field === 'description' || field === 'service_type' ? value : parseFloat(value as string) || 0,
    };
    setFormData((prev) => ({
      ...prev,
      line_items: newLineItems,
    }));
  };

  const addLineItem = () => {
    setFormData((prev) => ({
      ...prev,
      line_items: [
        ...prev.line_items,
        { description: '', quantity: 1, unit_price: 0, service_type: 'engineering' },
      ],
    }));
  };
  
  // Add project time entries as line items (broken down by week-ending and employee)
  const addProjectAsLineItems = (project: ProjectTimeDetail) => {
    // Create line items for each week-ending/employee combination
    const newLineItems: ExtendedLineItem[] = project.entries.map((entry: WeekEmployeeEntry) => ({
      description: `WE ${formatWeekEnding(entry.week_ending)} - ${entry.employee_name}`,
      quantity: entry.hours,
      unit_price: entry.rate,
      time_entry_ids: entry.time_entry_ids,
    }));
    
    setFormData((prev) => ({
      ...prev,
      line_items: [
        ...prev.line_items,
        ...newLineItems,
      ],
    }));
  };

  const removeLineItem = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index),
    }));
  };

  const calculateLineTotal = (item: LineItem) => {
    return item.quantity * item.unit_price;
  };

  const calculateSubtotal = () => {
    return formData.line_items.reduce((sum, item) => sum + calculateLineTotal(item), 0);
  };

  const calculateTaxAmount = () => {
    return calculateSubtotal() * (formData.tax_rate / 100);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTaxAmount();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  // Check if a project has any entries already added as line items
  const isProjectAlreadyAdded = (project: ProjectTimeDetail): boolean => {
    // Check if any of the project's entries' time_entry_ids are already in line items
    for (const entry of project.entries) {
      for (const entryId of entry.time_entry_ids) {
        const isAdded = formData.line_items.some(item => 
          item.time_entry_ids?.includes(entryId)
        );
        if (isAdded) return true;
      }
    }
    // Also check by description pattern (for backward compatibility)
    return formData.line_items.some(item => 
      item.description.includes(project.project_code)
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.line_items.length === 0) {
      setError('Please add at least one line item');
      return;
    }
    
    // Prepare data for submission - include time_entry_ids for backend linking
    const submitData: Invoice = {
      ...formData,
      line_items: formData.line_items.map(item => ({
        description: item.description,
        quantity: item.quantity,
        unit_price: item.unit_price,
        service_type: item.service_type || 'engineering',
        time_entry_ids: item.time_entry_ids || [],
      })),
    };
    
    try {
      setLoading(true);
      if (isEditMode && id) {
        await invoiceAPI.update(parseInt(id), submitData);
      } else {
        await invoiceAPI.create(submitData);
      }
      navigate('/invoices');
    } catch (err) {
      setError('Failed to save invoice');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="card">
        <h2>{isEditMode ? 'Edit Invoice' : 'Create New Invoice'}</h2>
        
        {error && <div className="error">{error}</div>}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="invoice_number">Invoice Number</label>
            <input
              type="text"
              id="invoice_number"
              name="invoice_number"
              value={formData.invoice_number || ''}
              placeholder="Draft — assigned on export to QuickBooks"
              onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value || undefined })}
            />
            <small style={{ color: '#666' }}>Leave blank to let QuickBooks assign on export, or enter manually</small>
          </div>

          <div className="form-group">
            <label htmlFor="purchase_order">Purchase Order *</label>
            <select
              id="purchase_order"
              name="purchase_order"
              value={formData.purchase_order}
              onChange={handleChange}
              required
            >
              <option value="">Select a purchase order</option>
              {pickerPurchaseOrders.map((po) => (
                <option key={po.id} value={po.id}>
                  {po.po_number} - {po.customer_details?.name}
                </option>
              ))}
            </select>
            {formData.purchase_order > 0 && (
              <small style={{ color: '#666', display: 'block', marginTop: '0.25rem' }}>
                {(() => {
                  const selectedPO = pickerPurchaseOrders.find(po => po.id === formData.purchase_order);
                  return selectedPO ? `Payment Terms: ${selectedPO.payment_terms_display}` : '';
                })()}
              </small>
            )}
          </div>

          <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              id="exclude_po_from_export"
              name="exclude_po_from_export"
              checked={formData.exclude_po_from_export || false}
              onChange={(e) => setFormData({ ...formData, exclude_po_from_export: e.target.checked })}
              style={{ width: 'auto' }}
            />
            <label htmlFor="exclude_po_from_export" style={{ margin: 0 }}>
              Exclude P.O. from QuickBooks export
            </label>
            <small style={{ color: '#666', marginLeft: '0.5rem' }}>
              Check this if the customer does not use purchase orders
            </small>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="form-group">
              <label htmlFor="issue_date">Issue Date *</label>
              <input
                type="date"
                id="issue_date"
                name="issue_date"
                value={formData.issue_date}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="due_date">Due Date *</label>
              <input
                type="date"
                id="due_date"
                name="due_date"
                value={formData.due_date}
                onChange={handleChange}
                required
              />
              <small style={{ color: '#666' }}>Auto-calculated from PO terms, can be overridden</small>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="form-group">
              <label htmlFor="start_date">Billing Period Start *</label>
              <input
                type="date"
                id="start_date"
                name="start_date"
                value={formData.start_date}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="end_date">Billing Period End *</label>
              <input
                type="date"
                id="end_date"
                name="end_date"
                value={formData.end_date}
                onChange={handleChange}
                required
              />
            </div>
          </div>
          
          {formData.purchase_order > 0 && timeEntriesDetail && timeEntriesDetail.projects.length > 0 && (
            <div style={{ 
              marginBottom: '1.5rem', 
              padding: '1rem', 
              backgroundColor: '#e8f5e9', 
              borderRadius: '8px',
              border: '2px solid #4caf50'
            }}>
              <h3 style={{ margin: '0 0 1rem 0', color: '#2e7d32' }}>
                Available Time Entries ({formData.start_date} to {formData.end_date})
              </h3>
              <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#555' }}>
                Click "Add to Invoice" to add a project's time entries as line items (grouped by week ending and employee)
              </p>
              {timeEntriesDetail.projects.filter(project => !isProjectAlreadyAdded(project)).map((project) => (
                <div 
                  key={project.project_id}
                  style={{ 
                    padding: '0.75rem',
                    marginBottom: '0.5rem',
                    backgroundColor: 'white',
                    borderRadius: '4px',
                    border: '1px solid #c8e6c9'
                  }}
                >
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '0.5rem'
                  }}>
                    <div>
                      <strong>{project.project_code}</strong> - {project.project_name}
                      <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.25rem' }}>
                        {project.total_hours} total hours • {project.entries.length} line item{project.entries.length !== 1 ? 's' : ''}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <span style={{ fontWeight: 'bold', color: '#2e7d32' }}>
                        {formatCurrency(project.total_amount)}
                      </span>
                      <button
                        type="button"
                        onClick={() => addProjectAsLineItems(project)}
                        className="btn btn-secondary"
                        style={{ fontSize: '0.85rem', padding: '0.4rem 0.8rem' }}
                      >
                        Add to Invoice
                      </button>
                    </div>
                  </div>
                  
                  {/* Preview of line items that will be added */}
                  <div style={{ 
                    fontSize: '0.8rem', 
                    color: '#666',
                    marginTop: '0.5rem',
                    paddingTop: '0.5rem',
                    borderTop: '1px solid #e0e0e0'
                  }}>
                    <strong>Preview:</strong>
                    <div style={{ marginTop: '0.25rem', maxHeight: '100px', overflowY: 'auto' }}>
                      {project.entries.slice(0, 5).map((entry, idx) => (
                        <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                          <span>WE {formatWeekEnding(entry.week_ending)} - {entry.employee_name}</span>
                          <span>{entry.hours}hrs @ {formatCurrency(entry.rate)}/hr = {formatCurrency(entry.amount)}</span>
                        </div>
                      ))}
                      {project.entries.length > 5 && (
                        <div style={{ fontStyle: 'italic', marginTop: '4px' }}>
                          ...and {project.entries.length - 5} more
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {timeEntriesDetail.projects.every(project => isProjectAlreadyAdded(project)) && (
                <div style={{ color: '#666', fontStyle: 'italic', textAlign: 'center', padding: '1rem' }}>
                  All available time entries have been added to the invoice.
                </div>
              )}
            </div>
          )}
          
          {loadingTimeEntries && formData.purchase_order > 0 && (
            <div style={{ 
              marginBottom: '1.5rem', 
              padding: '1rem', 
              backgroundColor: '#f5f5f5', 
              borderRadius: '8px',
              textAlign: 'center',
              color: '#666'
            }}>
              Loading time entries...
            </div>
          )}

          <div className="form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0 }}>Line Items</h3>
              <button type="button" onClick={addLineItem} className="btn btn-secondary">
                Add Line Item
              </button>
            </div>

            {formData.line_items.map((item, index) => (
              <div key={index} style={{ marginBottom: '1rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '4px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', gap: '1rem', alignItems: 'end' }}>
                  <div>
                    <label>Description *</label>
                    <input
                      type="text"
                      value={item.description}
                      onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                      required
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div>
                    <label>Hours *</label>
                    <input
                      type="number"
                      value={item.quantity}
                      onChange={(e) => handleLineItemChange(index, 'quantity', e.target.value)}
                      min="0"
                      step="0.01"
                      required
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div>
                    <label>Rate *</label>
                    <input
                      type="number"
                      value={item.unit_price}
                      onChange={(e) => handleLineItemChange(index, 'unit_price', e.target.value)}
                      min="0"
                      step="0.01"
                      required
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div>
                    <label>Type</label>
                    <select
                      value={item.service_type || 'engineering'}
                      onChange={(e) => handleLineItemChange(index, 'service_type', e.target.value)}
                      style={{ width: '100%' }}
                    >
                      <option value="engineering">Engineering</option>
                      <option value="reimbursement">Reimbursement</option>
                      <option value="hardware_delivery">Hardware Delivery</option>
                    </select>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeLineItem(index)}
                    className="btn btn-secondary"
                    style={{ marginTop: 0 }}
                  >
                    Remove
                  </button>
                </div>
                <div style={{ marginTop: '0.5rem', textAlign: 'right', fontWeight: 'bold' }}>
                  Total: {formatCurrency(calculateLineTotal(item))}
                </div>
              </div>
            ))}
          </div>

          <div className="form-group">
            <label htmlFor="tax_rate">Tax Rate (%)</label>
            <input
              type="number"
              id="tax_rate"
              name="tax_rate"
              value={formData.tax_rate}
              onChange={handleChange}
              min="0"
              step="0.01"
            />
          </div>

          <div className="form-group">
            <label htmlFor="status">Status *</label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              required
            >
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="notes">Notes</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes || ''}
              onChange={handleChange}
              rows={4}
            />
          </div>

          <div style={{ 
            marginTop: '2rem', 
            padding: '1rem', 
            backgroundColor: '#f5f5f5', 
            borderRadius: '8px' 
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span>Subtotal:</span>
              <span>{formatCurrency(calculateSubtotal())}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span>Tax ({formData.tax_rate}%):</span>
              <span>{formatCurrency(calculateTaxAmount())}</span>
            </div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              fontSize: '1.25rem', 
              fontWeight: 'bold',
              paddingTop: '0.5rem',
              borderTop: '2px solid #ddd'
            }}>
              <span>Total:</span>
              <span>{formatCurrency(calculateTotal())}</span>
            </div>
          </div>

          <div className="actions" style={{ marginTop: '2rem' }}>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : isEditMode ? 'Update Invoice' : 'Create Invoice'}
            </button>
            <button type="button" onClick={() => navigate('/invoices')} className="btn btn-secondary">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InvoiceForm;
